import {
    Button,
    Divider,
    Flex,
    MultiSelect,
    Pill,
    Popover,
    Text
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { FilterListOutlined } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { FC, useEffect, useRef } from "react";
import { defaultFileFilters, FileFilterState } from "./useFileFilters";

// Constants
const FILE_TYPE_OPTIONS = [
    { value: "IMAGE_UPLOAD", label: "Images" },
    { value: "VIDEO_UPLOAD", label: "Videos" },
    { value: "PDF_UPLOAD", label: "PDFs" },
    { value: "DOCUMENT_UPLOAD", label: "Documents" },
    { value: "CSV_UPLOAD", label: "CSVs" },
    { value: "ZIP_UPLOAD", label: "Archives" },
    { value: "OTHER_UPLOAD", label: "Other" }
];

// Section label style
const sectionLabelStyle = {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "var(--mantine-color-gray-6)",
    textTransform: "uppercase" as const,
    padding: "0.25rem 0.5rem 0.1rem"
};

type FileFiltersProps = {
    filters: FileFilterState;
    setFilters: (filters: FileFilterState) => void;
};

export const FileFilterButton: FC<FileFiltersProps> = ({
    filters,
    setFilters
}) => {
    // Data
    const hasActiveFilters = filters.fileTypes.length > 0;

    // State
    const [opened, { toggle, close }] = useDisclosure(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Handlers
    const clearFilters = () => setFilters(defaultFileFilters);

    // Manual click-outside handler
    useEffect(() => {
        if (!opened) return;

        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (buttonRef.current?.contains(target)) return;
            if (popoverRef.current?.contains(target)) return;

            const nestedPopoverDropdown = target.closest(
                ".mantine-Popover-dropdown"
            );
            if (
                nestedPopoverDropdown &&
                nestedPopoverDropdown !== popoverRef.current
            )
                return;

            close();
        };

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [opened, close]);

    return (
        <Popover
            opened={opened}
            position="bottom-start"
            shadow="md"
            trapFocus={false}
            closeOnClickOutside={false}
        >
            <Popover.Target>
                <Button
                    ref={buttonRef}
                    variant="default"
                    leftSection={<SvgIcon component={FilterListOutlined} />}
                    onClick={toggle}
                >
                    Filters
                </Button>
            </Popover.Target>
            <Popover.Dropdown
                ref={popoverRef}
                style={{
                    width: "18rem",
                    maxHeight: "20rem",
                    overflowY: "auto",
                    padding: "0.5rem 0"
                }}
            >
                {/* File Type */}
                <Text style={sectionLabelStyle}>File Type</Text>
                <div style={{ padding: "0 0.5rem 0.25rem" }}>
                    <MultiSelect
                        variant="pill"
                        placeholder="Select file types"
                        data={FILE_TYPE_OPTIONS}
                        value={filters.fileTypes}
                        onChange={(value) =>
                            setFilters({ ...filters, fileTypes: value })
                        }
                        comboboxProps={{ withinPortal: true }}
                        clearable
                        size="xs"
                    />
                </div>

                {/* Clear all */}
                {hasActiveFilters && (
                    <>
                        <Divider my={4} />
                        <Button
                            variant="subtle"
                            size="compact-xs"
                            c="dimmed"
                            fullWidth
                            onClick={clearFilters}
                        >
                            Clear filters
                        </Button>
                    </>
                )}
            </Popover.Dropdown>
        </Popover>
    );
};

export const FileFilterPills: FC<FileFiltersProps> = ({
    filters,
    setFilters
}) => {
    // Data
    const hasActiveFilters = filters.fileTypes.length > 0;
    if (!hasActiveFilters) return null;

    const pills = filters.fileTypes.map((ft) => ({
        key: "fileTypes",
        value: ft,
        label:
            FILE_TYPE_OPTIONS.find((o) => o.value === ft)?.label ?? ft
    }));

    const removePill = (value: string) => {
        setFilters({
            ...filters,
            fileTypes: filters.fileTypes.filter((ft) => ft !== value)
        });
    };

    return (
        <Flex gap={6} align="center" wrap="wrap">
            <Pill
                size="xs"
                style={{
                    cursor: "pointer",
                    backgroundColor: "var(--mantine-color-secondary-5)",
                    color: "white"
                }}
                onClick={() => setFilters(defaultFileFilters)}
            >
                Clear all
            </Pill>
            {pills.map((pill) => (
                <Pill
                    key={pill.value}
                    size="xs"
                    withRemoveButton
                    onRemove={() => removePill(pill.value)}
                >
                    {pill.label}
                </Pill>
            ))}
        </Flex>
    );
};
